// SMuFL (Standard Music Font Layout) glyph codepoints and the engraving
// metrics we read from them. Guitar Pro 8 uses this exact standard (libGPIO's
// Painter::bravuraGlyphBBox / SmuflUnicodes); we render standard notation from
// the Bravura font (SIL OFL), the SMuFL reference face. See SPEC.md §9.
//
// Codepoints below are the canonical SMuFL assignments. Metrics are expressed
// in staff-spaces (1 space = distance between two staff lines), matching our
// staff-space coordinate system so they scale with spaceSize at render time.

import { RhythmValue } from './rhythm';
import type { AccidentalKind } from './pitch';

export const SMUFL = {
  segno: 0xe047,
  fermataAbove: 0xe4c0,
  coda: 0xe048,
  gClef: 0xe050,
  /** Treble clef with an 8 below — guitar's clef (sounds an octave lower). */
  gClef8vb: 0xe052,
  fClef: 0xe062,
  cClef: 0xe05c,

  noteheadDoubleWhole: 0xe0a0,
  noteheadWhole: 0xe0a2,
  noteheadHalf: 0xe0a3,
  noteheadBlack: 0xe0a4,
  noteheadSlashVerticalEnds: 0xe100,
  noteheadSlashWhiteWhole: 0xe102,
  noteheadSlashWhiteHalf: 0xe103,
  noteheadSlashWhiteDoubleWhole: 0xe107,

  flag8thUp: 0xe240,
  flag8thDown: 0xe241,
  flag16thUp: 0xe242,
  flag16thDown: 0xe243,
  flag32ndUp: 0xe244,
  flag32ndDown: 0xe245,
  flag64thUp: 0xe246,
  flag64thDown: 0xe247,

  restWhole: 0xe4e3,
  restHalf: 0xe4e4,
  restQuarter: 0xe4e5,
  rest8th: 0xe4e6,
  rest16th: 0xe4e7,
  rest32nd: 0xe4e8,

  repeat1Bar: 0xe500,
  repeat2Bars: 0xe501,

  articAccentAbove: 0xe4a0,
  articAccentBelow: 0xe4a1,
  articStaccatoAbove: 0xe4a2,
  articStaccatoBelow: 0xe4a3,
  articTenutoAbove: 0xe4a4,
  articTenutoBelow: 0xe4a5,
  articMarcatoAbove: 0xe4ac,
  articMarcatoBelow: 0xe4ad,

  ornamentTrill: 0xe566,
  ornamentTurn: 0xe567,
  ornamentTurnInverted: 0xe568,
  ornamentShortTrill: 0xe56c,
  ornamentMordent: 0xe56d,

  dynamicPiano: 0xe520,
  dynamicMezzo: 0xe521,
  dynamicForte: 0xe522,
  dynamicPP: 0xe52b,
  dynamicMP: 0xe52c,
  dynamicMF: 0xe52d,
  dynamicFF: 0xe52f,

  accidentalFlat: 0xe260,
  accidentalNatural: 0xe261,
  accidentalSharp: 0xe262,
  accidentalDoubleSharp: 0xe263,
  accidentalDoubleFlat: 0xe264,

  augmentationDot: 0xe1e7,

  timeSig0: 0xe080, // digits 0..9 are contiguous: timeSig0 + n
  timeSigCommon: 0xe08a, // the C of common time (4/4)
  timeSigCutCommon: 0xe08b, // cut time (2/2)
} as const;

/** Ledger ink follows the actual head width and cue/grace size. */
export function ledgerHalfWidth(glyph: SmuflGlyph, scale = 1): number {
  const bounds = glyphHorizontalBounds(glyph);
  return (Math.max(-bounds.left, bounds.right) + ENGRAVING.ledgerExtension) * scale;
}

export type SmuflGlyph = number;

export interface GlyphVerticalBounds {
  /** Upper ink edge relative to the glyph anchor, in SVG's downward y direction. */
  top: number;
  /** Lower ink edge relative to the glyph anchor, in SVG's downward y direction. */
  bottom: number;
}

export interface GlyphHorizontalBounds {
  /** Ink edges relative to an SVG text glyph with text-anchor="middle". */
  left: number;
  right: number;
}

// Source: Bravura glyphBBoxes minus half glyphAdvanceWidths. SVG notation
// glyphs are centred on their advance, which can differ from their ink width.
const GLYPH_HORIZONTAL_BOUNDS: Partial<Record<SmuflGlyph, GlyphHorizontalBounds>> = {
  [SMUFL.accidentalFlat]: { left: -0.452, right: 0.452 },
  [SMUFL.accidentalNatural]: { left: -0.336, right: 0.336 },
  [SMUFL.accidentalSharp]: { left: -0.498, right: 0.498 },
  [SMUFL.accidentalDoubleSharp]: { left: -0.5, right: 0.488 },
  [SMUFL.accidentalDoubleFlat]: { left: -0.826, right: 0.818 },
  [SMUFL.noteheadDoubleWhole]: { left: -1.198, right: 1.198 },
  [SMUFL.noteheadWhole]: { left: -0.844, right: 0.844 },
  [SMUFL.noteheadHalf]: { left: -0.59, right: 0.59 },
  [SMUFL.noteheadBlack]: { left: -0.59, right: 0.59 },
  [SMUFL.noteheadSlashVerticalEnds]: { left: -0.73, right: 0.73 },
  [SMUFL.noteheadSlashWhiteWhole]: { left: -1.96, right: 1.96 },
  [SMUFL.noteheadSlashWhiteHalf]: { left: -1.56, right: 1.56 },
  [SMUFL.noteheadSlashWhiteDoubleWhole]: { left: -2.322, right: 2.322 },
  [SMUFL.restWhole]: { left: -0.566, right: 0.562 },
  [SMUFL.restHalf]: { left: -0.566, right: 0.562 },
  [SMUFL.restQuarter]: { left: -0.536, right: 0.54 },
  [SMUFL.rest8th]: { left: -0.5, right: 0.488 },
  [SMUFL.rest16th]: { left: -0.64, right: 0.64 },
  [SMUFL.rest32nd]: { left: -0.726, right: 0.726 },
  [SMUFL.articAccentAbove]: { left: -0.678, right: 0.678 },
  [SMUFL.articAccentBelow]: { left: -0.678, right: 0.678 },
  [SMUFL.articStaccatoAbove]: { left: -0.168, right: 0.168 },
  [SMUFL.articStaccatoBelow]: { left: -0.168, right: 0.168 },
  [SMUFL.articTenutoAbove]: { left: -0.68, right: 0.676 },
  [SMUFL.articTenutoBelow]: { left: -0.68, right: 0.676 },
  [SMUFL.articMarcatoAbove]: { left: -0.476, right: 0.468 },
  [SMUFL.articMarcatoBelow]: { left: -0.476, right: 0.468 },
  [SMUFL.ornamentTrill]: { left: -1.042, right: 1.042 },
  [SMUFL.ornamentTurn]: { left: -0.92, right: 0.92 },
  [SMUFL.ornamentTurnInverted]: { left: -0.926, right: 0.914 },
  [SMUFL.ornamentShortTrill]: { left: -1.46, right: 1.44 },
  [SMUFL.ornamentMordent]: { left: -1.454, right: 1.458 },
  [SMUFL.fermataAbove]: { left: -1.198, right: 1.21 },
};

export function glyphHorizontalBounds(glyph: SmuflGlyph): GlyphHorizontalBounds {
  return GLYPH_HORIZONTAL_BOUNDS[glyph] ?? { left: -2, right: 2 };
}

// Source: public/fonts/bravura_metadata.json, glyphBBoxes. Bravura's y axis
// points upward: SVG top = -bBoxNE[1], bottom = -bBoxSW[1]. Keep only the
// glyphs needed by notation collision clearance; the full font metadata
// should not become part of the application bundle.
const GLYPH_VERTICAL_BOUNDS: Partial<Record<SmuflGlyph, GlyphVerticalBounds>> = {
  [SMUFL.gClef]: { top: -4.392, bottom: 2.632 },
  [SMUFL.gClef8vb]: { top: -4.392, bottom: 3.512 },
  [SMUFL.fClef]: { top: -1.048, bottom: 2.54 },
  [SMUFL.cClef]: { top: -2.024, bottom: 2.024 },
  [SMUFL.noteheadDoubleWhole]: { top: -0.62, bottom: 0.62 },
  [SMUFL.noteheadWhole]: { top: -0.5, bottom: 0.5 },
  [SMUFL.noteheadHalf]: { top: -0.5, bottom: 0.5 },
  [SMUFL.noteheadBlack]: { top: -0.5, bottom: 0.5 },
  [SMUFL.noteheadSlashVerticalEnds]: { top: -0.996, bottom: 1.004 },
  [SMUFL.noteheadSlashWhiteWhole]: { top: -1, bottom: 1 },
  [SMUFL.noteheadSlashWhiteHalf]: { top: -1, bottom: 1 },
  [SMUFL.noteheadSlashWhiteDoubleWhole]: { top: -1, bottom: 1 },
  [SMUFL.flag8thUp]: { top: -0.03521239682756091, bottom: 3.240768470618394 },
  [SMUFL.flag8thDown]: { top: -3.232896633157715, bottom: 0.0575672 },
  [SMUFL.flag16thUp]: { top: -0.008, bottom: 3.252 },
  [SMUFL.flag16thDown]: { top: -3.2480256, bottom: 0.03601094374150052 },
  [SMUFL.flag32ndUp]: { top: -0.596, bottom: 3.248 },
  [SMUFL.flag32ndDown]: { top: -3.248, bottom: 0.687477099907407 },
  [SMUFL.flag64thUp]: { top: -1.387108, bottom: 3.248 },
  [SMUFL.flag64thDown]: { top: -3.248, bottom: 1.5040263329569774 },
  [SMUFL.accidentalFlat]: { top: -1.756, bottom: 0.7 },
  [SMUFL.accidentalNatural]: { top: -1.364, bottom: 1.34 },
  [SMUFL.accidentalSharp]: { top: -1.4, bottom: 1.392 },
  [SMUFL.accidentalDoubleSharp]: { top: -0.508, bottom: 0.5 },
  [SMUFL.accidentalDoubleFlat]: { top: -1.748, bottom: 0.7 },
  [SMUFL.restWhole]: { top: -0.036, bottom: 0.54 },
  [SMUFL.restHalf]: { top: -0.568, bottom: 0.008 },
  [SMUFL.restQuarter]: { top: -1.492, bottom: 1.5 },
  [SMUFL.rest8th]: { top: -0.696, bottom: 1.004 },
  [SMUFL.rest16th]: { top: -0.716, bottom: 2 },
  [SMUFL.rest32nd]: { top: -1.704, bottom: 2 },
  [SMUFL.articAccentAbove]: { top: -0.98, bottom: -0.004 },
  [SMUFL.articAccentBelow]: { top: 0, bottom: 0.976 },
  [SMUFL.articStaccatoAbove]: { top: -0.336, bottom: 0 },
  [SMUFL.articStaccatoBelow]: { top: 0, bottom: 0.336 },
  [SMUFL.articTenutoAbove]: { top: -0.192, bottom: 0 },
  [SMUFL.articTenutoBelow]: { top: 0, bottom: 0.192 },
  [SMUFL.articMarcatoAbove]: { top: -1.012, bottom: 0.004 },
  [SMUFL.articMarcatoBelow]: { top: 0, bottom: 1.016 },
  [SMUFL.ornamentTrill]: { top: -1.56, bottom: 0.04 },
  [SMUFL.ornamentTurn]: { top: -0.872, bottom: 0 },
  [SMUFL.ornamentTurnInverted]: { top: -0.872, bottom: 0 },
  [SMUFL.ornamentShortTrill]: { top: -0.98, bottom: 0 },
  [SMUFL.ornamentMordent]: { top: -1.276, bottom: 0.292 },
  [SMUFL.fermataAbove]: { top: -1.316, bottom: 0.012 },
};

// Unknown glyphs reserve a full Bravura em on each side of their anchor.
// This is a generous fallback, not a measured bound: add new note glyphs to
// the table above when introducing them so they receive precise clearance.
const UNKNOWN_GLYPH_VERTICAL_BOUNDS: GlyphVerticalBounds = { top: -4, bottom: 4 };

/** Visible notation glyph bounds at normal size, in staff-space offsets. */
export function glyphVerticalBounds(glyph: SmuflGlyph): GlyphVerticalBounds {
  return GLYPH_VERTICAL_BOUNDS[glyph] ?? UNKNOWN_GLYPH_VERTICAL_BOUNDS;
}

/** Shared by tuplet placement and rendering so numeral ink and hooks clear notes. */
export const TUPLET_ENGRAVING = {
  fontSize: 2.2,
  halfHeight: 1.1,
  hookLength: 0.7,
  clearance: 0.5,
  numberGap: 1.1,
} as const;

/** The printable character for a SMuFL codepoint (for SVG <text>). */
export function glyphChar(cp: SmuflGlyph): string {
  return String.fromCodePoint(cp);
}

/** Time-signature digit glyph (0..9). */
export function timeSigDigit(n: number): SmuflGlyph {
  if (n < 0 || n > 9) throw new Error(`timeSigDigit: ${n} not a single digit`);
  return SMUFL.timeSig0 + n;
}

/** A whole time-signature number as digit glyphs (12 → "1" "2"), laid side by
 *  side by the font's own advances. 12/8 and 7/16 are ordinary meters. */
export function timeSigNumber(n: number): string {
  return String(Math.max(0, Math.trunc(n)))
    .split('')
    .map((d) => glyphChar(timeSigDigit(Number(d))))
    .join('');
}

/** Notehead glyph for a note value (filled vs open vs whole). */
/** Rhythmic slash heads retain white heads for half/whole durations. */
export function slashNoteheadFor(value: RhythmValue): { glyph: SmuflGlyph; width: number } {
  if (value <= RhythmValue.Breve) return { glyph: SMUFL.noteheadSlashWhiteDoubleWhole, width: 4.644 };
  if (value === RhythmValue.Whole) return { glyph: SMUFL.noteheadSlashWhiteWhole, width: 3.92 };
  if (value === RhythmValue.Half) return { glyph: SMUFL.noteheadSlashWhiteHalf, width: 3.12 };
  return { glyph: SMUFL.noteheadSlashVerticalEnds, width: 1.46 };
}

export function noteheadFor(value: RhythmValue): SmuflGlyph {
  if (value <= RhythmValue.Breve) return SMUFL.noteheadDoubleWhole;
  if (value === RhythmValue.Whole) return SMUFL.noteheadWhole;
  if (value === RhythmValue.Half) return SMUFL.noteheadHalf;
  return SMUFL.noteheadBlack; // quarter and shorter
}

/** Flag glyph for an unbeamed value, or null if it takes no flag. */
export function flagFor(value: RhythmValue, stemUp: boolean): SmuflGlyph | null {
  switch (value) {
    case RhythmValue.Eighth:
      return stemUp ? SMUFL.flag8thUp : SMUFL.flag8thDown;
    case RhythmValue.Sixteenth:
      return stemUp ? SMUFL.flag16thUp : SMUFL.flag16thDown;
    case RhythmValue.ThirtySecond:
      return stemUp ? SMUFL.flag32ndUp : SMUFL.flag32ndDown;
    case RhythmValue.SixtyFourth:
      return stemUp ? SMUFL.flag64thUp : SMUFL.flag64thDown;
    default:
      return null;
  }
}

/** Rest glyph for a note value. */
export function restFor(value: RhythmValue): SmuflGlyph {
  switch (value) {
    case RhythmValue.Whole:
      return SMUFL.restWhole;
    case RhythmValue.Half:
      return SMUFL.restHalf;
    case RhythmValue.Quarter:
      return SMUFL.restQuarter;
    case RhythmValue.Eighth:
      return SMUFL.rest8th;
    case RhythmValue.Sixteenth:
      return SMUFL.rest16th;
    default:
      return SMUFL.rest32nd;
  }
}

/** Articulation glyph, on the placement side. */
export function articulationGlyph(
  kind: 'staccato' | 'accent' | 'tenuto' | 'marcato',
  above: boolean,
): SmuflGlyph {
  switch (kind) {
    case 'staccato':
      return above ? SMUFL.articStaccatoAbove : SMUFL.articStaccatoBelow;
    case 'accent':
      return above ? SMUFL.articAccentAbove : SMUFL.articAccentBelow;
    case 'tenuto':
      return above ? SMUFL.articTenutoAbove : SMUFL.articTenutoBelow;
    case 'marcato':
      return above ? SMUFL.articMarcatoAbove : SMUFL.articMarcatoBelow;
  }
}

/** Ornament symbol → its SMuFL glyph. MusicXML naming: <mordent> is the
 *  lower mordent (with the vertical stroke), <inverted-mordent> the
 *  pralltriller (SMuFL's "short trill"). */
export function ornamentGlyph(
  kind: 'trill' | 'turn' | 'inverted-turn' | 'mordent' | 'inverted-mordent' | 'fermata',
): SmuflGlyph {
  switch (kind) {
    case 'trill':
      return SMUFL.ornamentTrill;
    case 'fermata':
      return SMUFL.fermataAbove;
    case 'turn':
      return SMUFL.ornamentTurn;
    case 'inverted-turn':
      return SMUFL.ornamentTurnInverted;
    case 'mordent':
      return SMUFL.ornamentMordent;
    case 'inverted-mordent':
      return SMUFL.ornamentShortTrill;
  }
}

/** Dynamic mark → its SMuFL glyph run (single combined glyphs where Bravura
 *  has them, letter glyphs otherwise). */
export function dynamicGlyphs(mark: string): SmuflGlyph[] | null {
  switch (mark) {
    case 'pp':
      return [SMUFL.dynamicPP];
    case 'p':
      return [SMUFL.dynamicPiano];
    case 'mp':
      return [SMUFL.dynamicMP];
    case 'mf':
      return [SMUFL.dynamicMF];
    case 'f':
      return [SMUFL.dynamicForte];
    case 'ff':
      return [SMUFL.dynamicFF];
    case 'fp':
      return [SMUFL.dynamicForte, SMUFL.dynamicPiano];
    case 'sf':
    case 'sfz':
      return [SMUFL.dynamicForte]; // closest simple form
    default:
      return null;
  }
}

/** Accidental glyph for a spelling accidental. */
export function accidentalGlyph(kind: AccidentalKind | 'natural'): SmuflGlyph {
  switch (kind) {
    case 'sharp':
      return SMUFL.accidentalSharp;
    case 'flat':
      return SMUFL.accidentalFlat;
    case 'natural':
      return SMUFL.accidentalNatural;
  }
}

/**
 * Engraving metrics in staff-spaces. These are the Bravura values we depend on;
 * a later pass can load them from bravura_metadata.json instead of hardcoding
 * (GP reads them from the font at runtime — Painter::bravuraGlyphBBox). Keeping
 * them here as named constants means one place to reconcile against the font.
 */
export const ENGRAVING = {
  /** Black notehead width (glyph advance). */
  noteheadWidth: 1.18,
  /** Whole notehead width (Bravura bbox) — used to offset seconds in stacks. */
  noteheadWholeWidth: 1.7,
  /** Default stem length from notehead centre. */
  stemLength: 3.5,
  /** Stem line thickness. */
  stemThickness: 0.12,
  /** Beam thickness. */
  beamThickness: 0.5,
  /** Vertical gap between stacked beams. */
  beamSpacing: 0.25,
  /** Keep the beam's total rise modest even when pitches leap. */
  beamSlopeMax: 1.0,
  /** Below this slope, flatten the beam to horizontal (GP beamSlopeMin). */
  beamSlopeMin: 0.25,
  /** Staff line thickness. */
  staffLineThickness: 0.13,
  /** Ledger line length beyond the notehead on each side. */
  ledgerExtension: 0.4,
  /** Advance added per augmentation dot. */
  dotAdvance: 0.5,
  /** Grace-note size: glyph and stem-anchor scale (engraver AND renderer). */
  graceScale: 0.65,
  /** Grace stems run proportionally a touch longer than the head scale. */
  graceStemScale: 0.72,
  /** Cue-note size: glyph and stem-anchor scale (engraver AND renderer). */
  cueScale: 0.7,
} as const;
